import dayjs from "dayjs";
import { TranslationStore } from "../../i18n/translation-store";
import { ServiceInterface } from "../service.interface";
import {
  reaction,
  action,
  computed,
  IReactionDisposer,
  observable,
} from "mobx";
import { PreferenceService } from "../preference";
import { ApiService } from "../api";
import { Translation } from "../../i18n/translation";
import { DefaultLogger } from "../../logger";
import { createContext } from "react";
import { DeviceService } from "../device";
import { TransTokens } from "../../i18n/trans-tokens";
import { Environment } from "@citychallenge/common";
import { UserService } from "../user";

export interface SupportedLanguage {
  value: string;
  label: Translation;
}

export const I18nServiceContext = createContext<I18nService>(null as any);

export class I18nService implements ServiceInterface {
  public context = I18nServiceContext;

  constructor(
    private apiService: ApiService,
    private preferenceService: PreferenceService,
    private userService: UserService,
    private deviceService: DeviceService,
    public readonly store = TranslationStore
  ) {}

  private translations = this.preferenceService.STORED_TRANSLATIONS;
  private lastRefresh?: Date;

  private subscriptions: IReactionDisposer[] = [];

  public async serviceDidBecomeForeground() {
    // Wait until dependent preferences are ready
    await Promise.all([
      this.preferenceService.PREF_LANGUAGE.ready$,
      this.preferenceService.STORED_TRANSLATIONS.ready$,
    ]);

    // Refresh stores on activelanguage switch
    this.subscriptions.push(
      reaction(
        () => this.activeLanguage,
        () => this.refreshStores(),
        {
          fireImmediately: true,
        }
      )
    );

    // Triggered by the account locale alone, on purpose. Tracking whether the
    // locale is offered would re-run this whenever a surface declares or drops
    // its languages — the pages shell clears them on unmount — re-asserting the
    // account locale over a choice the visitor made on this device.
    this.subscriptions.push(
      reaction(
        () => this.userService.user?.locale,
        (locale) => {
          if (!locale) return;

          if (
            locale !== this.preferredLanguage &&
            this.offeredLanguages.some((v) => v.value === locale)
          ) {
            this.setPreferredLanguage(locale);
          }
        }
      )
    );

    this.refreshIfStale();
  }

  public async serviceDidBecomeBackground() {
    for (const dispose of this.subscriptions) dispose();
  }

  public getLanguage(iso: string) {
    return this.translations.value?.find((t) => t.id === iso);
  }

  /** Every language the platform has enabled, whatever the brand defaults to. */
  @computed
  public get enabledLanguages(): SupportedLanguage[] {
    return (this.translations.value || [])
      .filter(({ enabled }) => !!enabled)
      .map(({ id, name }) => ({
        value: id,
        label: TransTokens.getTranslation(name),
      }));
  }

  /**
   * What a surface offers when nobody has chosen: the brand's own set.
   * EnergyPlatform ships EN/NL copy, so it defaults to those two — a project
   * that configures its languages resolves against {@link enabledLanguages}
   * instead and can go beyond this.
   */
  @computed
  public get supportedLanguages(): SupportedLanguage[] {
    if (Environment.identifier !== "nl.energyplatform")
      return this.enabledLanguages;

    return this.enabledLanguages.filter(
      ({ value }) => value === "en" || value === "nl"
    );
  }

  public isLanguageSupported(iso: string) {
    return !!this.getLanguage(iso);
  }

  /** Unset means unconstrained; a surface that limits its languages sets these. */
  @observable private availableLanguages?: string[];

  @action
  public setAvailableLanguages(languages?: string[]) {
    this.availableLanguages = languages?.length ? languages : undefined;
  }

  private isLanguageAvailable(iso: string) {
    return !this.availableLanguages || this.availableLanguages.includes(iso);
  }

  /**
   * The languages this surface actually offers: what it declared, else the
   * brand default. A project offering a language the brand doesn't ship still
   * gets it — that's the point of the setting.
   */
  @computed
  private get offeredLanguages() {
    if (!this.availableLanguages) return this.supportedLanguages;
    return this.enabledLanguages.filter(({ value }) =>
      this.isLanguageAvailable(value)
    );
  }

  @computed
  public get deviceDefaultLanguage() {
    const offered = this.offeredLanguages;

    if (this.deviceService.getPreferredLanguages) {
      const preferred = this.deviceService.getPreferredLanguages() || [];

      for (const { full, short } of preferred) {
        const v =
          offered.find(({ value }) => value === full) ||
          offered.find(({ value }) => value.split("-")[0] === short);

        if (v) return v.value;
      }
    }

    const activeLanguage = this.deviceService.getDeviceLanguage();
    if (this.isLanguageAvailable(activeLanguage.short)) {
      return activeLanguage.short;
    }

    return offered[0]?.value ?? activeLanguage.short;
  }

  /**
   * The preferred game language of the user
   */
  @computed
  public get preferredLanguage() {
    const stored = this.preferenceService.PREF_LANGUAGE.value;
    if (stored && this.isLanguageAvailable(stored)) return stored;

    return this.deviceDefaultLanguage;
  }

  @computed
  public get savedPreferredLanguage() {
    return this.preferenceService.PREF_LANGUAGE.value;
  }

  @observable
  public priorityLanguage?: string;

  @action
  public setPriorityLanguage(lang?: string) {
    this.priorityLanguage = lang;
  }

  @computed
  public get activeLanguage() {
    return this.priorityLanguage || this.preferredLanguage;
  }

  @action
  public setPreferredLanguage(lang: string) {
    this.preferenceService.PREF_LANGUAGE.setValue(lang);
  }

  private refreshStores() {
    let iso = this.activeLanguage;
    if (!this.isLanguageSupported(iso)) iso = "en";

    const language = this.getLanguage(iso);
    if (!language) TranslationStore.resetTranslations();
    else TranslationStore.setTranslations(language.translations || {});

    const newLanguage = language?.id || "en";
    TranslationStore.setActiveLanguage(newLanguage);

    try {
      // Safely set locale, because this might crash the app if locale is not
      // available at all (used to be the case because metero tree shaking may
      // remove some locales)
      dayjs.locale(newLanguage);
    } catch (e) {
      DefaultLogger.captureException(e);
    }
  }

  private refreshIfStale = async () => {
    // Refresh translations after one hour of inactivity
    const inactivityDurationMs = 60 * 60 * 1000;

    if (
      !this.lastRefresh ||
      +new Date() - +this.lastRefresh > inactivityDurationMs
    ) {
      try {
        const { data } = await this.apiService.clientPublic.getTranslations();
        this.translations.setValue(data);
        this.refreshStores();
        this.lastRefresh = new Date();
      } catch (e) {}
    }
  };
}
